import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../components/Layout';
import { getInventoryItems, getStockBatches, getItemTypes, getWarehouses, getInventoryRecipients, getInventoryKnownCities, getMerchants, transferInventory, downloadTransferBulkTemplate, bulkUploadTransfer } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Truck, Send, Upload, Download, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import InventoryRoleMappingDialog from '../../components/InventoryRoleMappingDialog';

const TARGET_ROLES = [
  { value: 'state_head', label: 'State Head' },
  { value: 'asm', label: 'ASM' },
  { value: 'tl', label: 'Team Lead' },
  { value: 'fse', label: 'Field Sales Executive (FSE)' },
];

const TransferInventory = ({ user, setUser }) => {
  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [recipients, setRecipients] = useState([]);
  
  const [tab, setTab] = useState('serialized');
  
  // Serialized form state
  const [selectedItems, setSelectedItems] = useState(new Set());
  
  // Bulk form state
  const [selectedBatch, setSelectedBatch] = useState('');
  const [bulkQty, setBulkQty] = useState(1);
  
  // Common target state
  const [toHolderType, setToHolderType] = useState('asm');
  const [toHolderId, setToHolderId] = useState('');
  const [targetRole, setTargetRole] = useState('asm');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [merchantSearch, setMerchantSearch] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  // Distribution context (Round 37) — City / Source Warehouse / Transaction Date
  const [distCity, setDistCity] = useState('');
  const [distSrcWarehouseId, setDistSrcWarehouseId] = useState('');
  const [distTxnDate, setDistTxnDate] = useState('');
  const [knownCities, setKnownCities] = useState([]);

  // Bulk transfer state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Role Mapping dialog
  const [roleMappingOpen, setRoleMappingOpen] = useState(false);

  const downloadTemplate = async () => {
    try {
      const r = await downloadTransferBulkTemplate();
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'inventory_transfer_template.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Failed to download template'); }
  };

  const submitBulkExcel = async () => {
    if (!bulkFile) { toast.error('Please choose a file first'); return; }
    try {
      setBulkUploading(true);
      const fd = new FormData(); fd.append('file', bulkFile);
      const r = await bulkUploadTransfer(fd);
      setBulkResult(r.data);
      const total = (r.data.transferred_serialized || 0) + (r.data.transferred_bulk || 0);
      if (total > 0) toast.success(`Transferred ${r.data.transferred_serialized} serialized + ${r.data.transferred_bulk} batches`);
      if (r.data.errors?.length > 0) toast.warning(`${r.data.errors.length} rows had errors`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setBulkUploading(false); }
  };

  const isPrivileged = ['admin', 'hr_manager', 'warehouse_manager'].includes(user?.role);

  const load = async () => {
    try {
      const [i, b, t, w, m, c] = await Promise.all([
        getInventoryItems({ limit: 1000 }),
        getStockBatches({}),
        getItemTypes(),
        getWarehouses(),
        getMerchants({ limit: 500 }),
        getInventoryKnownCities(),
      ]);
      setItems(i.data); setBatches(b.data); setItemTypes(t.data); setWarehouses(w.data); setMerchants(m.data);
      setKnownCities(c.data?.cities || []);
    } catch { toast.error('Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  // Load recipients when target role changes
  const refreshRecipients = () => {
    if (toHolderType === 'merchant' || toHolderType === 'warehouse' || toHolderType === 'scrap_bin') {
      setRecipients([]);
      return;
    }
    getInventoryRecipients({ role: targetRole }).then(r => setRecipients(r.data)).catch(() => setRecipients([]));
  };
  useEffect(() => {
    refreshRecipients();
  }, [toHolderType, targetRole]);

  const typeName = (id) => itemTypes.find(t => t.id === id)?.name || '-';

  const transferableItems = useMemo(() => {
    // Items currently held by current user (or all if admin) and not deployed/scrapped/lost
    const blocked = new Set(['deployed', 'scrapped', 'lost', 'returned']);
    return items.filter(i => !blocked.has(i.status));
  }, [items]);

  const transferableBatches = useMemo(() => batches.filter(b => b.quantity_remaining > 0), [batches]);

  const toggle = (id) => {
    const s = new Set(selectedItems);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedItems(s);
  };

  const buildTargetSelector = () => {
    if (toHolderType === 'warehouse') {
      const q = warehouseSearch.trim().toLowerCase();
      const filtered = q
        ? warehouses.filter(w => (`${w.name} ${w.location || ''} ${w.state || ''}`).toLowerCase().includes(q))
        : warehouses;
      return (
        <div className="space-y-2">
          <Input
            placeholder="Search warehouses..."
            value={warehouseSearch}
            onChange={e => setWarehouseSearch(e.target.value)}
            className="h-8 text-sm"
            data-testid="warehouse-search"
          />
          <Select value={toHolderId} onValueChange={setToHolderId}>
            <SelectTrigger data-testid="target-warehouse"><SelectValue placeholder="Choose warehouse..." /></SelectTrigger>
            <SelectContent>
              {filtered.map(w => <SelectItem key={w.id} value={w.id}>{w.name} - {w.location}</SelectItem>)}
              {filtered.length === 0 && <div className="p-3 text-xs text-slate-500 text-center">No matches</div>}
            </SelectContent>
          </Select>
        </div>
      );
    }
    if (toHolderType === 'merchant') {
      const q = merchantSearch.trim().toLowerCase();
      const filtered = q
        ? merchants.filter(m => (`${m.name} ${m.merchant_code || ''} ${m.phone || ''} ${m.city || ''}`).toLowerCase().includes(q))
        : merchants;
      return (
        <div className="space-y-2">
          <Input
            placeholder="Search merchants..."
            value={merchantSearch}
            onChange={e => setMerchantSearch(e.target.value)}
            className="h-8 text-sm"
            data-testid="merchant-search"
          />
          <Select value={toHolderId} onValueChange={setToHolderId}>
            <SelectTrigger data-testid="target-merchant"><SelectValue placeholder="Choose merchant..." /></SelectTrigger>
            <SelectContent>
              {filtered.map(m => <SelectItem key={m.id} value={m.id}>{m.name} ({m.merchant_code})</SelectItem>)}
              {filtered.length === 0 && <div className="p-3 text-xs text-slate-500 text-center">No matches</div>}
            </SelectContent>
          </Select>
        </div>
      );
    }
    if (toHolderType === 'scrap_bin') {
      return <p className="text-xs text-slate-500">Items will be marked as scrapped</p>;
    }
    const q = recipientSearch.trim().toLowerCase();
    const filteredRecipients = q
      ? recipients.filter(u => (`${u.full_name || ''} ${u.email || ''}`).toLowerCase().includes(q))
      : recipients;
    return (
      <div className="space-y-2">
        <Select value={targetRole} onValueChange={(v) => { setTargetRole(v); setRecipientSearch(''); }}>
          <SelectTrigger data-testid="target-role"><SelectValue /></SelectTrigger>
          <SelectContent>{TARGET_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
        <Input
          placeholder={`Search ${TARGET_ROLES.find(r => r.value === targetRole)?.label || targetRole} by name or email...`}
          value={recipientSearch}
          onChange={e => setRecipientSearch(e.target.value)}
          className="h-8 text-sm"
          data-testid="recipient-search"
        />
        <Select value={toHolderId} onValueChange={setToHolderId}>
          <SelectTrigger data-testid="target-user"><SelectValue placeholder={`Choose ${TARGET_ROLES.find(r => r.value === targetRole)?.label || targetRole}...`} /></SelectTrigger>
          <SelectContent>
            {filteredRecipients.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}
            {filteredRecipients.length === 0 && (
              <div className="p-3 text-xs text-slate-500 text-center">
                {recipients.length === 0 ? 'No recipients for this role' : `No matches for "${recipientSearch}"`}
              </div>
            )}
          </SelectContent>
        </Select>
        {q && (
          <p className="text-[11px] text-slate-500">
            Showing {filteredRecipients.length} of {recipients.length}
          </p>
        )}
      </div>
    );
  };

  const submitSerialized = async () => {
    if (selectedItems.size === 0) { toast.error('Select at least one item'); return; }
    if (!toHolderId && toHolderType !== 'scrap_bin') { toast.error('Choose target'); return; }
    
    const target = toHolderType === 'scrap_bin' ? null : 
      toHolderType === 'warehouse' ? warehouses.find(w => w.id === toHolderId) :
      toHolderType === 'merchant' ? merchants.find(m => m.id === toHolderId) :
      recipients.find(r => r.id === toHolderId);
    
    try {
      const movementType = toHolderType === 'merchant' ? 'deploy' : toHolderType === 'scrap_bin' ? 'scrap' : 'transfer';
      await transferInventory({
        item_type: 'serialized',
        item_ids: Array.from(selectedItems),
        to_holder_type: toHolderType,
        to_holder_id: toHolderType === 'scrap_bin' ? 'scrap_bin' : toHolderId,
        to_holder_name: target ? (target.name || target.full_name) : 'Scrap Bin',
        movement_type: movementType,
        reason,
        notes,
        city: distCity || null,
        source_warehouse_id: distSrcWarehouseId || null,
        transaction_date: distTxnDate || null,
      });
      toast.success(`Transferred ${selectedItems.size} item(s)`);
      setSelectedItems(new Set()); setReason(''); setNotes('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Transfer failed'); }
  };

  const submitBulk = async () => {
    if (!selectedBatch || !bulkQty) { toast.error('Select batch and quantity'); return; }
    if (!toHolderId && toHolderType !== 'scrap_bin') { toast.error('Choose target'); return; }
    const target = toHolderType === 'warehouse' ? warehouses.find(w => w.id === toHolderId) :
      toHolderType === 'merchant' ? merchants.find(m => m.id === toHolderId) :
      recipients.find(r => r.id === toHolderId);
    
    try {
      await transferInventory({
        item_type: 'bulk',
        batch_id: selectedBatch,
        quantity: parseInt(bulkQty),
        to_holder_type: toHolderType,
        to_holder_id: toHolderId,
        to_holder_name: target ? (target.name || target.full_name) : '',
        movement_type: toHolderType === 'merchant' ? 'deploy' : 'transfer',
        reason, notes,
        city: distCity || null,
        source_warehouse_id: distSrcWarehouseId || null,
        transaction_date: distTxnDate || null,
      });
      toast.success('Transferred');
      setBulkQty(1); setSelectedBatch(''); setReason(''); setNotes('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Transfer failed'); }
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Transfer / Issue Inventory</h1>
          <p className="text-sm text-slate-500">Move inventory down the chain: Warehouse → State Head → ASM → TL → FSE → Merchant</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadTemplate} data-testid="bt-download-tmpl" size="sm"><Download className="h-4 w-4 mr-1" />Template</Button>
          <Button onClick={() => { setBulkOpen(true); setBulkResult(null); setBulkFile(null); }} data-testid="bt-open-bulk" size="sm"><Upload className="h-4 w-4 mr-1" />Bulk Transfer (Excel)</Button>
          {isPrivileged && (
            <Button variant="outline" onClick={() => setRoleMappingOpen(true)} data-testid="open-role-mapping-btn" size="sm" title="Map HRMS designations to inventory role dropdowns">
              <Settings2 className="h-4 w-4 mr-1" />Role Mapping
            </Button>
          )}
        </div>
      </div>

        {/* Target panel — shared between tabs */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Send className="h-4 w-4" />Target Recipient</CardTitle></CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Target Type</Label>
                <Select value={toHolderType} onValueChange={v => { setToHolderType(v); setToHolderId(''); if (['state_head','asm','tl','fse'].includes(v)) setTargetRole(v); }}>
                  <SelectTrigger data-testid="target-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {isPrivileged && <SelectItem value="warehouse">Warehouse</SelectItem>}
                    <SelectItem value="state_head">State Head</SelectItem>
                    <SelectItem value="asm">ASM</SelectItem>
                    <SelectItem value="tl">Team Lead</SelectItem>
                    <SelectItem value="fse">Field Sales Executive</SelectItem>
                    <SelectItem value="merchant">Merchant (Deploy)</SelectItem>
                    {isPrivileged && <SelectItem value="scrap_bin">Scrap Bin</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Target</Label>
                {buildTargetSelector()}
              </div>
              <div><Label>Reason</Label><Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Deployment / Issue / Return / Replace" data-testid="reason" /></div>
              <div className="md:col-span-2"><Label>Notes</Label><Input value={notes} onChange={e => setNotes(e.target.value)} data-testid="notes" /></div>
            </div>

            {/* Distribution Context (Round 37) — City / Source Warehouse / Backdate */}
            <div className="mt-4 rounded-md border border-indigo-200 bg-indigo-50/40 p-3">
              <div className="text-xs font-semibold text-indigo-800 mb-2">Distribution Context <span className="text-[10px] text-slate-500 font-normal">(optional — audit metadata)</span></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[11px]">City</Label>
                  <Select value={distCity || '__none__'} onValueChange={(v) => setDistCity(v === '__none__' ? '' : v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid="dist-city"><SelectValue placeholder="Auto-fill from target…" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="__none__" className="text-xs italic text-slate-400">Auto-fill from target</SelectItem>
                      {knownCities.map(c => (
                        <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px]">Source Warehouse</Label>
                  <Select value={distSrcWarehouseId || '__none__'} onValueChange={(v) => setDistSrcWarehouseId(v === '__none__' ? '' : v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid="dist-src-warehouse"><SelectValue placeholder="From item&apos;s current holder" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="__none__" className="text-xs italic text-slate-400">Use item&apos;s current holder</SelectItem>
                      {warehouses.map(w => (
                        <SelectItem key={w.id} value={w.id} className="text-xs">{w.name}{w.city ? ` — ${w.city}` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px]">Transaction Date <span className="text-[10px] text-slate-400">(last 365d)</span></Label>
                  <Input
                    type="date"
                    className="h-8 text-xs"
                    max={new Date().toISOString().slice(0, 10)}
                    min={new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)}
                    value={distTxnDate ? distTxnDate.slice(0, 10) : ''}
                    onChange={(e) => setDistTxnDate(e.target.value ? `${e.target.value}T00:00:00Z` : '')}
                    data-testid="dist-txn-date"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="serialized" data-testid="tab-serial">Serialized Items</TabsTrigger>
            <TabsTrigger value="bulk" data-testid="tab-bulk">Bulk Batches</TabsTrigger>
          </TabsList>

          <TabsContent value="serialized">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Select Items to Transfer</CardTitle>
                <Button onClick={submitSerialized} disabled={selectedItems.size === 0} data-testid="serial-transfer-btn"><Truck className="h-4 w-4 mr-1" />Transfer Selected ({selectedItems.size})</Button>
              </CardHeader>
              <CardContent>
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="p-2 w-10"></th>
                        <th className="text-left p-2 text-xs font-medium">Serial #</th>
                        <th className="text-left p-2 text-xs font-medium">Item Type</th>
                        <th className="text-left p-2 text-xs font-medium">Status</th>
                        <th className="text-left p-2 text-xs font-medium">Held By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transferableItems.map(i => (
                        <tr key={i.id} className="border-b hover:bg-slate-50">
                          <td className="p-2"><Checkbox checked={selectedItems.has(i.id)} onCheckedChange={() => toggle(i.id)} data-testid={`pick-${i.id}`} /></td>
                          <td className="p-2 font-mono text-xs text-indigo-700">{i.serial_number}</td>
                          <td className="p-2">{typeName(i.item_type_id)}</td>
                          <td className="p-2"><span className="px-2 py-0.5 text-xs rounded bg-slate-100">{i.status}</span></td>
                          <td className="p-2 text-xs">{i.holder_name}</td>
                        </tr>
                      ))}
                      {transferableItems.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-slate-500">No transferable items available</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bulk">
            <Card>
              <CardHeader><CardTitle className="text-base">Pick Bulk Batch</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <Label>Batch</Label>
                    <Select value={selectedBatch} onValueChange={setSelectedBatch}>
                      <SelectTrigger data-testid="bulk-batch"><SelectValue placeholder="Choose batch..." /></SelectTrigger>
                      <SelectContent>{transferableBatches.map(b => (
                        <SelectItem key={b.id} value={b.id}>{typeName(b.item_type_id)} — {b.holder_name} ({b.quantity_remaining} left)</SelectItem>
                      ))}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Quantity</Label><Input type="number" min="1" value={bulkQty} onChange={e => setBulkQty(e.target.value)} data-testid="bulk-qty" /></div>
                </div>
                <Button onClick={submitBulk} disabled={!selectedBatch || !bulkQty} data-testid="bulk-transfer-btn"><Truck className="h-4 w-4 mr-1" />Transfer</Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Bulk Transfer Dialog */}
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Bulk Transfer from Excel</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="rounded-lg border bg-blue-50 border-blue-200 p-3 text-sm">
                <p className="font-medium text-blue-900">Step 1 — Download Template</p>
                <p className="text-xs text-blue-700 mt-1">3 sheets: Bulk Transfers (sample rows), Target Types, Instructions. Mix serialized and bulk in one file.</p>
                <Button size="sm" variant="outline" className="mt-2" onClick={downloadTemplate} data-testid="bt-dialog-download"><Download className="h-4 w-4 mr-1" />Download Template</Button>
              </div>

              <div className="rounded-lg border bg-slate-50 border-slate-200 p-3 text-sm">
                <p className="font-medium text-slate-900">Step 2 — Upload Filled File</p>
                <p className="text-xs text-slate-600 mt-1">For serialized rows: provide serial number + target. For bulk rows: provide Item Type + Source Warehouse + Quantity + target.</p>
                <Input type="file" accept=".xlsx,.xls" onChange={e => { setBulkFile(e.target.files[0]); setBulkResult(null); }} className="mt-2" data-testid="bt-file" />
              </div>

              {bulkResult && (
                <div className="rounded-lg border p-3 text-sm">
                  <p className="font-semibold text-green-700">
                    ✓ Transferred {bulkResult.transferred_serialized || 0} serialized + {bulkResult.transferred_bulk || 0} bulk
                  </p>
                  {bulkResult.errors?.length > 0 && (
                    <div className="mt-2">
                      <p className="font-semibold text-red-700 mb-1">{bulkResult.errors.length} error{bulkResult.errors.length === 1 ? '' : 's'}:</p>
                      <div className="max-h-40 overflow-y-auto bg-red-50 p-2 rounded text-xs">
                        {bulkResult.errors.map((e, i) => <div key={i} className="text-red-800">Row {e.row}: {e.error}</div>)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setBulkOpen(false); if (bulkResult) load(); }}>Close</Button>
              <Button onClick={submitBulkExcel} disabled={!bulkFile || bulkUploading} data-testid="bt-confirm">
                {bulkUploading ? 'Uploading...' : 'Upload & Transfer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <InventoryRoleMappingDialog
          open={roleMappingOpen}
          onOpenChange={setRoleMappingOpen}
          onSaved={refreshRecipients}
        />
      </div>
    </Layout>
  );
};

export default TransferInventory;
